/**
 * Calculates the difference in days between two dates.
 * Uses UTC midnight for both dates to avoid timezone-related discrepancies.
 *
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Object containing the absolute day difference (rounded up) and totalDays
 *
 * @remarks
 * - The calculation is always positive (uses Math.abs)
 * - Days are rounded up using Math.ceil, so partial days count as full days
 * - Both dates are normalized to UTC midnight before calculation
 *
 * @example
 * const result = getDaysBetween(new Date('2025-01-01'), new Date('2025-01-15'))
 * console.log(result.totalDays) // 14
 */
export function getDaysBetween(startDate: Date, endDate: Date) {
  // Normalize both dates to UTC midnight to avoid timezone issues
  const startUtcMidnight = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate())
  const endUtcMidnight = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate())

  // Calculate absolute difference in milliseconds
  const timeDifferenceMs = Math.abs(endUtcMidnight - startUtcMidnight)

  // Convert to days and round up
  const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24
  const totalDays = Math.ceil(timeDifferenceMs / MILLISECONDS_PER_DAY)

  return {
    totalDays,
  }
}
